import type { GuideStep } from '../guide/types';

/**
 * Zrzuty ekranów do instrukcji obsługi (28.10.2026, S-28.10-02) — ekrany,
 * których nie pokazuje żaden przewodnik po zmianach (lista zleceń, karta
 * zlecenia, prezlecenia…). Nagrywa je ta sama nagrywarka co slajdy
 * przewodników (`e2e/guides/record.mjs`, pod kluczem `manual`), na tych
 * samych danych pokazowych, więc instrukcja odświeża się przy każdym nagraniu:
 * `/guides/slides/manual/<n>.png`, n = miejsce na liście od 1.
 *
 * Plik importuje wyłącznie typy — nagrywarka i skrypt PDF transpilują go bez
 * reszty aplikacji. Nowy zrzut dopisuj **na końcu** (numer pliku = miejsce
 * na liście). Kroki jak w przewodnikach: tylko otwierają ekran, nic nie
 * zapisują.
 */

/** Klucz katalogu slajdów instrukcji w manifeście nagrywarki. */
export const MANUAL_SLIDES_KEY = 'manual';

export interface ManualShot extends GuideStep {
  /** Identyfikator, po którym treść instrukcji wskazuje zrzut. */
  id: string;
}

const screen = (
  id: string,
  title: string,
  route: string,
  account: GuideStep['recorder']['account'],
  extra: Partial<Pick<ManualShot, 'record' | 'target'>> & { demo?: GuideStep['recorder']['demo'] } = {},
): ManualShot => ({
  id,
  route,
  target: extra.target ?? 'wsp-tresc',
  title,
  description: title,
  action: 'Zrzut ekranu do instrukcji.',
  record: extra.record,
  recorder: { account, demo: extra.demo, noHighlight: extra.target === undefined },
});

export const MANUAL_SHOTS: readonly ManualShot[] = [
  screen('dashboard', 'Pulpit po zalogowaniu', '/', 'master'),
  screen('menu', 'Menu boczne', '/', 'ftl', { target: 'wsp-menu-user-offers' }),
  screen('bank', 'Lista zleceń', '/user-offers', 'ftl'),
  screen('order-card', 'Karta zlecenia', '/user-offers/:orderId/show', 'master', {
    demo: { kind: 'order', number: 'DEMO/2026/10/H03' },
    record: { description: 'dowolne zlecenie z listy zleceń', listPath: '/user-offers' },
  }),
  screen('fleet-schedule', 'Harmonogram floty', '/fleet-schedule', 'ftl'),
  screen('car-card', 'Karta pojazdu', '/cars/:carId/show', 'ftl', {
    demo: { kind: 'car', plate: 'DEMO 001' },
    record: { description: 'dowolny pojazd z listy samochodów', listPath: '/cars' },
  }),
  screen('cars', 'Lista samochodów', '/cars', 'ftl'),
  screen('pre-orders', 'Lista prezleceń', '/pre-orders', 'cs'),
  screen('pre-order-card', 'Karta prezlecenia', '/pre-orders/:preOrderId/show', 'cs', {
    demo: { kind: 'scenario', key: 'b1PreOrderId' },
    record: { description: 'dowolne prezlecenie z listy', listPath: '/pre-orders' },
  }),
  screen('via-orders', 'Zlecenia VIA', '/via-orders', 'adm'),
  screen('publication-queue', 'Kolejka publikacji', '/postings', 'trade'),
  screen('carriers', 'Lista przewoźników', '/carriers', 'trade'),
  screen('users', 'Użytkownicy', '/users', 'admin'),
  screen('feature-flags', 'Przełączniki funkcji', '/feature-flags', 'admin'),
  // Bez wskazanego elementu (30.10.2026): ekran od góry, bez ramki — z ramką nagrywarka przewijała do środka
  // instrukcji i nagłówek rozdziału wychodził ucięty pod paskiem.
  screen('manual', 'Instrukcja w aplikacji', '/manual', 'ftl'),
];

/** Miejsce zrzutu na liście (od 0) albo -1. */
export function shotIndex(id: string): number {
  return MANUAL_SHOTS.findIndex((s) => s.id === id);
}
